import React, { useEffect, useState } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { HelpVideo } from '../../../navigation/layouts/Lori/types'
import { TrainingVideoController } from '../../../hooks/useTrainingVideo'

const useStyles = makeStyles((theme: Theme) => ({
    formControl: {
        minWidth: 300,
        width: '100%',
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
    },
    videoNumber: {
        marginRight: theme.spacing(2),
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
}))

interface Props {
    open: boolean
    onClose: () => void
    trainingVideoController: TrainingVideoController
}

export const InitialTrainingVideosModal = ({
    open,
    onClose,
    trainingVideoController,
}: Props) => {
    const classes = useStyles()
    const [selectedVideos, setSelectedVideos] = useState<(HelpVideo | null)[]>([
        null,
        null,
        null,
        null,
    ])

    useEffect(() => {
        if (open) {
            trainingVideoController.getInitialTrainingVideos()
        }
    }, [open])

    useEffect(() => {
        if (trainingVideoController.initialTrainingVideos.length > 0 && open) {
            const sortedVideos = [
                ...trainingVideoController.initialTrainingVideos,
            ]
                .sort((a, b) => a.order - b.order)
                .map((item) => item.training_video) as (HelpVideo | null)[]

            // Always ensure exactly 4 slots
            const newVideos = Array(4).fill(null)
            sortedVideos.forEach((video, index) => {
                if (index < 4) newVideos[index] = video
            })

            setSelectedVideos(newVideos)
        }
    }, [trainingVideoController.initialTrainingVideos, open])

    const handleVideoChange = (index: number, video: HelpVideo | null) => {
        const newSelectedVideos = [...selectedVideos]
        newSelectedVideos[index] = video
        setSelectedVideos(newSelectedVideos)
    }

    const handleSave = () => {
        const videos = selectedVideos
            .map((video, index) => ({
                training_video: video?.id || 0,
                order: index + 1,
            }))
            .filter((v) => v.training_video !== 0)

        trainingVideoController.updateInitialTrainingVideos({ videos })
        onClose()
    }

    const handleClose = () => {
        setSelectedVideos([null, null, null, null])
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Set Initial Training Videos</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Select the videos that will be shown in the initial training
                    sequence. The order here determines the order they will
                    appear to users.
                </Typography>
                <List>
                    {selectedVideos.map((selectedVideo, index) => (
                        <ListItem key={index} className={classes.listItem}>
                            <Typography className={classes.videoNumber}>
                                {index + 1}
                            </Typography>
                            <FormControl className={classes.formControl}>
                                <InputLabel>
                                    Training Video {index + 1}
                                </InputLabel>
                                <Select
                                    value={selectedVideo?.id || ''}
                                    onChange={(e) => {
                                        const videoId = e.target.value as number
                                        const video = trainingVideoController.videoLibrary.find(
                                            (v) => v.id === videoId,
                                        )
                                        handleVideoChange(index, video || null)
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {trainingVideoController.videoLibrary.map(
                                        (video) => (
                                            <MenuItem
                                                key={video.id}
                                                value={video.id}
                                                disabled={selectedVideos.some(
                                                    (v, i) =>
                                                        i !== index &&
                                                        v?.id === video.id,
                                                )}
                                            >
                                                {video.title}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={!selectedVideos.some((v) => v !== null)}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}
